import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, onMounted, PropType, Ref, ref, watch } from "vue";
import {
  AllocStatusCode,
  ArrivalTimeOptionCode,
  BusStopEventCode,
  PROVIDE_KEY_ARRIVAL_TIME_REMAINING,
  PROVIDE_KEY_NEXT_BUS_STOP_CHANGE_DATE_TIME,
} from "@/types";
import { IBusStop } from "@/domain/entities/busStop";
import {
  IArrivalTimeOption,
  IBus,
  IBusLocation,
  IBusStopEvent,
} from "@/domain/entities/lineInfo";
import { useLineStore } from "@/stores/lineStore";
import useGetBusStopStartTime from "@/use/useGetBusStopStartTime";
import useGetBusStopEndTime from "@/use/useGetBusStopEndTime";
import TransferBusStop from "./TransferBusStop.vue";
import InlineSvg from "vue-inline-svg";
import { IArrivalTime } from "@/domain/entities/lineInfo/arrivalTime.types";
import BusLocationIndicator from "./BusLocationIndicator.vue";
import FavoriteStatusButton from "../main/FavoriteStatusButton.vue";
import dayjs from "dayjs";
import BusStopChangeTextAndModal from "./BusStopChangeTextAndModal.vue";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";


const __default__ = {
  name: "BusStopRow",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  index: {
    type: Number,
    required: true,
  },
  selectedArrivalTimeOption: {
    type: Object as PropType<IArrivalTimeOption>,
  },
  selectedBus: {
    type: Object as PropType<IBusLocation>,
  },
  allBusesMap: {
    type: Object as PropType<Map<string, IBus>>,
  },
  busStop: {
    type: Object as PropType<IBusStop>,
    required: true,
  },
  lineId: {
    type: String,
    required: true,
  },
  turnId: {
    type: String,
    required: true,
  },
  boardingBusStop: {
    type: Object as PropType<IBusStop | undefined>,
    default: undefined,
  },
  arrivalBusStop: {
    type: Object as PropType<IBusStop | undefined>,
    default: undefined,
  },
  selectComplete: {
    type: Boolean,
    default: false,
  },
  busLocations: {
    type: Array as PropType<IBusLocation[]>,
  },
  showInMainCard: {
    type: Boolean,
    default: false,
  },
  showBottomDivider: {
    type: Boolean,
    default: true,
  },
  isInsideShuttle: {
    type: Boolean,
    default: false,
  },
  isBackwardLine: {
    type: Boolean,
    default: false,
  },
  firstBusStopId: {
    type: String,
  },
  isLastRow: {
    type: Boolean,
    default: false,
  },
  nextBusStopId: {
    type: String,
  },
  isBookable: {
    type: Boolean,
  },
  isOnBoarding: {
    type: Boolean,
    default: false,
  },
  activatedGetOffBell: {
    type: Boolean,
    default: false,
  },
  arrivalTimesForDisplay: {
    type: Object as PropType<IArrivalTime>,
  },
  favoriteGetOnBusStop: {
    type: Object as PropType<IBusStop>,
  },
  favoriteGetOffBusStop: {
    type: Object as PropType<IBusStop>,
  },
  detourEndDtm: {
    type: String,
  },
  shouldShowBusLocations: {
    type: Boolean,
    default: true,
  },
  shouldShowGetOffBell: {
    type: Boolean,
    default: false,
  },
  shouldShowBusStopLocation: {
    type: Boolean,
    default: false,
  },
},
  emits: ["toggleSelectBusStop", "onClickBusStopLocation", "onClickGetOffBell", "onClickScheduleBtn", "onClickBubble", "onClickFavoriteStatusButton", "onClickBusIcon"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

dayjs.extend(isSameOrBefore);

const props = __props;

const emit = __emit;

const emitOnClickFavoriteStatusButton = (busStop: IBusStop) => {
  emit("onClickFavoriteStatusButton", busStop);
};

const emitOnClickBusIcon = (busesImageExisted: IBus[]) => {
  emit("onClickBusIcon", busesImageExisted);
};

const emitOnClickBusStopLocation = (busStop: IBusStop) => {
  emit("onClickBusStopLocation", busStop);
};

const emitOnClickBubble = () => {
  emit("onClickBubble");
};

const lineStore = useLineStore();

const lineInfo = computed(() => {
  const lineInfo = lineStore.getLineInfoByLineId(props.lineId);
  return lineInfo;
});

const nextBusStopChangeDateTime = inject(
  PROVIDE_KEY_NEXT_BUS_STOP_CHANGE_DATE_TIME
) as Ref<string | undefined> | undefined;

/** 정류장 변경 예정 문구 및 모달 표시 여부 */
const shouldShowBusStopChangeTextAndModal = computed(() => {
  // 변경(추가/폐지) 예정 일자가 없으면, 문구 표시 안함
  if (!nextBusStopChangeDateTime?.value) {
    return false;
  }

  const { useBeginDtm, useEndDtm } = props.busStop;
  const busStopDates = [useBeginDtm, useEndDtm].filter(Boolean);

  // 정류장의 사용 시작/종료 일시가 회차의 사용시작일시보다 같거나 이전인 경우, 문구 표시 안함
  const turnUseBeginDtm = lineStore.getTurnUseBeginDtm(props.turnId);
  const isAllBeforeOrSame = busStopDates.every((dtm) =>
    dayjs(dtm).isSameOrBefore(turnUseBeginDtm, "minute")
  );
  if (isAllBeforeOrSame) return false;

  // 변경(추가/폐지) 예정인 정류장이 있으며 해당 변경일자와 일치하는 경우에만, 문구 표시 함
  return busStopDates.some((date) =>
    dayjs(date).isSame(nextBusStopChangeDateTime.value, "minute")
  );
});

const busStopTypeName = computed(() => {
  if (props.busStop.isGetOnStop && props.busStop.isGetOffStop) {
    return "승차/하차";
  } else if (props.busStop.isGetOnStop && props.busStop.isGetOffStop) {
    return "";
  }
  return props.busStop.isGetOnStop ? "승차" : "하차";
});

const detourEndDisplayText = computed(() =>
  props.detourEndDtm
    ? dayjs(props.detourEndDtm).format("YYYY-MM-DD HH:mm")
    : "우회 종료시"
);

const shouldShowPrefixSvg = computed(
  () =>
    !!props.arrivalTimesForDisplay &&
    props.arrivalTimesForDisplay.prefix.endsWith(".svg")
);

const busStopTimeText = computed(() => {
  if (props.isInsideShuttle) {
    return "";
  }

  if (
    props.selectedArrivalTimeOption?.type === ArrivalTimeOptionCode.STANDARD &&
    props.isBackwardLine &&
    !props.busStop.isGetOnStop &&
    props.busStop.isGetOffStop
  ) {
    return "";
  }

  if (props.busStop.isPassByStop()) {
    return "미정차";
  }

  if (props.selectedArrivalTimeOption?.type === ArrivalTimeOptionCode.TMAP) {
    return props.arrivalTimesForDisplay?.time ?? "";
  }

  if (props.selectedArrivalTimeOption?.type === ArrivalTimeOptionCode.AVG) {
    return `${props.arrivalTimesForDisplay?.prefix ?? ""}${
      props.arrivalTimesForDisplay?.time ?? ""
    }`;
  }

  // 사내 셔틀
  if (props.isInsideShuttle) {
    if (props.showInMainCard) return "";

    const startTime = useGetBusStopStartTime(lineInfo.value, props.busStop.id);
    const endTime = useGetBusStopEndTime(lineInfo.value, props.busStop.id);
    if (startTime === endTime) {
      return `${startTime}`;
    }
    return `${startTime}~${endTime}`;
  }

  // 이하 목표도착시각
  return `${props.busStop.departureTime}`;
});

const isFavoriteBusStop = computed(() => {
  const { isGetOffStop, isGetOnStop } = props.busStop;

  if (isGetOffStop && isGetOnStop) {
    return (
      props.favoriteGetOnBusStop?.id === props.busStop.id ||
      props.favoriteGetOffBusStop?.id === props.busStop.id
    );
  }

  if (isGetOnStop) {
    return props.favoriteGetOnBusStop?.id === props.busStop.id;
  }

  if (isGetOffStop) {
    return props.favoriteGetOffBusStop?.id === props.busStop.id;
  }

  return false;
});

const buttonTitle = ref(props.busStop.isGetOnStop ? "승차" : "하차");

const selected = ref(false);
const toggleSelectBusStop = (offsetY: number) => {
  if (!props.isBookable || props.isInsideShuttle) return;

  selected.value = !selected.value;
  emit("toggleSelectBusStop", {
    busStop: props.busStop,
    selected: selected.value,
    offsetY,
  });
};
const selectButton = ref<HTMLElement>();

const shouldShowRemainigTime = computed(
  () =>
    props.showInMainCard &&
    !!props.busStop.isMyBoardingStop &&
    !props.isInsideShuttle &&
    props.isBookable
);

const arrivalTimeRemaining = computed(() => {
  if (!props.showInMainCard) return "";
  const remainingTimeStr = inject(
    PROVIDE_KEY_ARRIVAL_TIME_REMAINING
  ) as Ref<string>;
  return remainingTimeStr.value === "정보없음" ? "" : remainingTimeStr.value;
});

const isInsideShuttleAndLastRow = computed(
  () => props.isInsideShuttle && props.isLastRow
);

const busLocationsMap = computed(() => {
  if (!props.shouldShowBusLocations) return new Map();

  return props.isInsideShuttle
    ? getShuttleBusLocationsMap()
    : getBusLocationsMap();
});

const isBusStopEventExisted = (event: BusStopEventCode) => {
  return (
    event !== BusStopEventCode.None &&
    event !== BusStopEventCode.LastLeave &&
    !!busLocationsMap.value.get(event)?.length
  );
};

const hasBusEndedDrivingOrArrivedLastStop = (location: IBusLocation) => {
  const lastBusStop = location.busStopEvents[location.busStopEvents.length - 1];

  return (
    location.allocStatusCode === AllocStatusCode.DRIVEN ||
    lastBusStop.busStopEventCode === BusStopEventCode.LastLeave
  );
};

const hasSameBusStopId = (event: IBusStopEvent) => {
  return event.uid === props.busStop.id;
};

const getBusLocationsMap = () => {
  const busLocationsMap: Map<BusStopEventCode, IBusLocation[]> = new Map();

  for (const location of props.busLocations ?? []) {
    // 버스가 운행종료 상태이거나, 마지막 정류장에 도착한 경우는 무시한다.
    if (hasBusEndedDrivingOrArrivedLastStop(location)) continue;

    location.busStopEvents.forEach((event) => {
      if (!hasSameBusStopId(event)) return;

      const busLocation = busLocationsMap.get(event.busStopEventCode) ?? [];
      busLocationsMap.set(event.busStopEventCode, [...busLocation, location]);
    });
  }

  return busLocationsMap;
};

const getNextEvent = (index: number, location: IBusLocation) => {
  const eventLength = location.busStopEvents.length;
  return index + 1 < eventLength
    ? location.busStopEvents[index + 1]
    : undefined;
};

const getShuttleBusLocationsMap = () => {
  const busLocationsMap: Map<BusStopEventCode, IBusLocation[]> = new Map();
  if (isInsideShuttleAndLastRow.value) return busLocationsMap;

  for (const location of props.busLocations ?? []) {
    // 버스가 운행종료 상태이거나, 마지막 정류장에 도착한 경우는 무시한다.
    if (hasBusEndedDrivingOrArrivedLastStop(location)) continue;

    location.busStopEvents.forEach((event, index) => {
      const nextEvent = getNextEvent(index, location);
      if (!hasSameBusStopId(event)) return;
      if (!props.busStop.isReturnStop && nextEvent?.uid !== props.nextBusStopId)
        return;

      const busLocation = busLocationsMap.get(event.busStopEventCode) ?? [];
      busLocationsMap.set(event.busStopEventCode, [...busLocation, location]);
    });
  }

  return busLocationsMap;
};

const updateButton = () => {
  if (
    props.busStop.id === props.boardingBusStop?.id ||
    props.busStop.id === props.arrivalBusStop?.id
  ) {
    selectButton.value?.classList.add("selected");
    selected.value = true;
  } else {
    selectButton.value?.classList.remove("selected");
    selected.value = false;
  }
  updateDestinationArrow();
  updateSelectButton();
};

// TODO: 누른 셀에 대해서는 일단 selected 처리... 성능 확인할것
watch(
  () => [props.boardingBusStop, props.arrivalBusStop],
  () => {
    if (!props.isBookable || props.isInsideShuttle) return;
    updateButton();
  }
);

const showArrowStart = ref(false);
const showArrowMiddle = ref(false);
const showArrowEnd = ref(false);

const updateDestinationArrow = () => {
  showArrowStart.value =
    props.selectComplete &&
    props.busStop.id === props.boardingBusStop?.id &&
    props.busStop.isGetOnStop;

  showArrowMiddle.value =
    props.selectComplete &&
    props.busStop.id !== props.boardingBusStop?.id &&
    props.busStop.id !== props.arrivalBusStop?.id &&
    props.busStop.id !== props.arrivalBusStop?.id &&
    props.busStop.order! > props.boardingBusStop!.order! &&
    props.busStop.order! < props.arrivalBusStop!.order!;

  showArrowEnd.value =
    props.selectComplete &&
    props.busStop.id === props.arrivalBusStop?.id &&
    props.busStop.isGetOffStop;
};

const showButton = ref(props.busStop.isGetOnStop || props.busStop.isGetOffStop);
const updateSelectButton = () => {
  if (props.busStop.isPassByStop()) return false;

  if (props.selectComplete) {
    showButton.value =
      props.busStop.id === props.boardingBusStop?.id ||
      props.busStop.id === props.arrivalBusStop?.id;
  } else {
    showButton.value = true;
  }
};

// Row text dimmed 처리
const disable = computed(
  () => isOutsideSelectionRange.value || isPassByBusStop.value
);
const isOutsideSelectionRange = computed(
  () =>
    props.selectComplete &&
    !showArrowStart.value &&
    !showArrowMiddle.value &&
    !showArrowEnd.value
);
const isPassByBusStop = computed(() => props.busStop.isPassByStop());

// ToDo: 하차벨 상태 및 클릭 이벤트 처리
const emitOnClickGetOffBell = (activate: boolean) => {
  emit("onClickGetOffBell", props.busStop.id, activate);
};

const emitOnClickScheduleBtn = (busStop: IBusStop) => {
  emit("onClickScheduleBtn", busStop);
};

onMounted(() => {
  if (props.boardingBusStop?.id && props.arrivalBusStop?.id) {
    updateButton();
  }
});

const __returned__ = { props, emit, emitOnClickFavoriteStatusButton, emitOnClickBusIcon, emitOnClickBusStopLocation, emitOnClickBubble, lineStore, lineInfo, nextBusStopChangeDateTime, shouldShowBusStopChangeTextAndModal, busStopTypeName, detourEndDisplayText, shouldShowPrefixSvg, busStopTimeText, isFavoriteBusStop, buttonTitle, selected, toggleSelectBusStop, selectButton, shouldShowRemainigTime, arrivalTimeRemaining, isInsideShuttleAndLastRow, busLocationsMap, isBusStopEventExisted, hasBusEndedDrivingOrArrivedLastStop, hasSameBusStopId, getBusLocationsMap, getNextEvent, getShuttleBusLocationsMap, updateButton, showArrowStart, showArrowMiddle, showArrowEnd, updateDestinationArrow, showButton, updateSelectButton, disable, isOutsideSelectionRange, isPassByBusStop, emitOnClickGetOffBell, emitOnClickScheduleBtn, TransferBusStop, get InlineSvg() { return InlineSvg }, BusLocationIndicator, FavoriteStatusButton, BusStopChangeTextAndModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})